import { useCallback, useMemo, useState } from 'react'
import { useRenewal } from 'src/context/renewal'
import { useCustomer } from 'src/context/customer'
import { RenewalSection } from 'src/dtos/renewalData'
import {
  ApplicationBodyContainer,
  ApplicationBodyContent,
  DefaultTitle,
  DefaultSubtitle,
  FormContainer,
  FormItemContainer,
  Link,
  OptionButtonsContainer,
} from 'src/components/application/defaultComponents'
import { ContainerWithLeftBar, FlexDiv, FormInputErrorMessage } from 'src/components/application/formComponents/styles'
import PrebuiltRenewalFooter from 'src/components/application/PrebuiltRenewalFooter'
import StepBullet from 'src/components/basic/StepBullet'
import OptionText from 'src/components/application/formComponents/OptionText'
import StandardInput from 'src/components/basic/StandardInput'
import OptionButton from 'src/components/basic/OptionButton'
import { LeftBar } from 'src/components/basic/LeftBar'
import { tlieSources } from 'src/constants/tlie'
import ReminderCard from 'src/components/basic/ReminderCard'
import FilePicker from 'src/components/basic/FilePicker'
import { ValidationError } from 'yup'
import { uploadFiles } from 'src/services/apiClient'
import { allActiveAttorneys, hasICOCAttorneys, isClaimsTriggered } from 'src/utils/renewals'
import { FirmQuestionsMainSchema } from 'src/yup/RenewalSchema'
import { ValidationErrorMessage } from 'src/components/basic/ValidationErrorMessage/ValidationErrorMessage'
import { useErrors, ValidationError as ServerValidationError } from 'src/context/errors'
import {FILE_SIZE_LABEL} from "../../../utils/constants";
import {
  CYBER_WEBSITE_MAX_LENGTH,
  EMPLOYEES_MAX_DIGITS,
  MSG_EMPLOYEES_FORMAT,
  MSG_EMPLOYEES_REQUIRED,
  MSG_REVENUE_FORMAT,
  MSG_REVENUE_REQUIRED,
  MSG_WEBSITE_FORMAT,
  MSG_WEBSITE_LENGTH,
  MSG_WEBSITE_REQUIRED,
  NON_NEGATIVE_INTEGER,
  POSITIVE_INTEGER,
  REVENUE_MAX_DIGITS,
  isValidCyberWebsite,
  normalizeCyberWebsite,
} from 'src/utils/cyberQuestions'
type QuestionsData = Omit<RenewalSection, "hasLeavingAttorneys" | "departingAttorneys" | "newAttorneys">

// Builds a full ValidationError object from just a code + message, filling the
// remaining fields with inert defaults so setValidationErrors leaves it untouched.
const buildCyberError = (ErrorCode: string, ErrorMessage: string): ServerValidationError => ({
  ErrorCode,
  ErrorMessage,
  PropertyName: '',
  AttemptedValue: '',
  CustomState: null,
  Severity: 0,
  FormattedMessagePlaceholderValues: {},
})

const Questions = () => {
  const { firmData } = useCustomer()
  const { renewalData, saveRenewalData } = useRenewal()
  const { triggerToast, clearToast, errors } = useErrors()
  const [questions, setQuestions] = useState<QuestionsData>(renewalData?.renewalSection ?? {})
  const [revenueInput, setRevenueInput] = useState<string>(renewalData?.renewalSection?.cyberEstimatedAnnualRevenue?.toString() ?? '')
  const [employeesInput, setEmployeesInput] = useState<string>(renewalData?.renewalSection?.cyberTotalEmployees?.toString() ?? '')
  // Raw mirror, like revenueInput/employeesInput. The website is never rewritten mid-keystroke —
  // normalizing on every change would eat characters as the user types "https://". Seeded normalized
  // because rows saved under the old permissive rule read back scheme-prefixed. Falls back to the
  // page-1 firm website when that already satisfies the strict rule, so most firms never retype it;
  // an unusable page-1 value (e.g. "firm.com") is deliberately not prefilled.
  const [websiteInput, setWebsiteInput] = useState<string>(() => {
    const saved = normalizeCyberWebsite(renewalData?.renewalSection?.cyberFirmWebsiteUrl)
    if(saved !== '') return saved

    const fromFirmProfile = normalizeCyberWebsite(renewalData?.firmSection?.contactInfo?.website)
    return isValidCyberWebsite(fromFirmProfile) ? fromFirmProfile : ''
  })
  const [websiteBlurred, setWebsiteBlurred] = useState(false)
  const [showErrors, setShowErrors] = useState(false)
  const stepOneErrors = ['EC65', 'EC66'];
  const stepTwoErrors = ['EC67', 'EC68']
  const stepFourErrors = ['EC70', 'EC71']
  const stepFiveErrors = ['EC72', 'EC73']
  const stepTwelveErrors = ['EC86', 'EC87']

  // Single source of truth for "is the website answer wrong, and why". Consumed by the step13
  // navigation gate, the validation-panel builder and the inline message, so those three can
  // never disagree. Purely derived, so it re-evaluates on every keystroke — which is what lets
  // the error clear the moment the user fixes it.
  const normalizedWebsite = normalizeCyberWebsite(websiteInput)
  const cyberWebsiteError: 'required' | 'format' | 'length' | null =
    questions?.wantsStandaloneCyberQuote !== true ? null
      : normalizedWebsite === '' ? 'required'
      // Length is checked against the NORMALIZED value, because that is what is POSTed and what the
      // server's Length(1,99) rule sees — a raw "https://" prefix does not count against the cap.
      : normalizedWebsite.length > CYBER_WEBSITE_MAX_LENGTH ? 'length'
      : isValidCyberWebsite(normalizedWebsite) ? null
      : 'format'

  const cyberWebsiteErrorMessage =
    cyberWebsiteError === 'required' ? MSG_WEBSITE_REQUIRED
      : cyberWebsiteError === 'length' ? MSG_WEBSITE_LENGTH
      : cyberWebsiteError === 'format' ? MSG_WEBSITE_FORMAT
      : ''

  const updatedRenewalData = () => ({
    ...renewalData,
    currentPage: window.location.pathname,
    renewalSection: {
      ...renewalData?.renewalSection,
      ...questions,
      // Set explicitly after the spread so the value that is validated, the value that gates
      // navigation and the value that is POSTed are one function of one state in one render.
      // "Save for later" runs no client validation at all, so persistence correctness must not
      // depend on blur having fired first.
      cyberFirmWebsiteUrl: questions?.wantsStandaloneCyberQuote === true
        ? (normalizedWebsite === '' ? undefined : normalizedWebsite)
        : undefined,
    }
  })

  const validatePage = useCallback(() => {
    let errors: string[] = []
    try {
      FirmQuestionsMainSchema.validateSync({
        ...questions,
      }, { abortEarly: false })
    } catch(e) {
      errors = (e as ValidationError).errors
    }
    // The Yes/No itself is required; and when the answer is Yes, revenue must be a positive whole
    // number, the employee count a whole number of 0 or greater (no commas/decimals/letters either
    // way), and the website present and well-formed. Without the unanswered case the server's EC335
    // was the only thing enforcing the question, so Save & Continue let the applicant through and
    // then failed the request.
    if(!errors.includes('step13') &&
      (questions?.wantsStandaloneCyberQuote == null ||
        (questions?.wantsStandaloneCyberQuote === true &&
          (!POSITIVE_INTEGER.test(revenueInput.trim()) || !NON_NEGATIVE_INTEGER.test(employeesInput.trim()) ||
            cyberWebsiteError !== null)))) {
      errors.push('step13')
    }
    return errors
  }, [questions, revenueInput, employeesInput, cyberWebsiteError])

  const validationPerStep = useMemo(() => {
    const errorsArray: string[] = validatePage()
    let validationObject: {[key: string]: boolean} = {}

    for(let step = 1; step <= 13; step++) {
      if(errorsArray.length > 0)
        validationObject[`step${step}`] = !errorsArray.includes(`step${step}`)
      else
        validationObject[`step${step}`] = true
    }
    return validationObject
  }, [validatePage])

  const buildCyberErrors = (): ServerValidationError[] => {
    if(questions?.wantsStandaloneCyberQuote !== true) return []

    const cyberErrors: ServerValidationError[] = []
    // DOM order EC336 -> EC337 -> EC338 so the panel auto-scrolls to revenue first
    if(!POSITIVE_INTEGER.test(revenueInput.trim())) {
      cyberErrors.push(buildCyberError('EC336', revenueInput.trim() === '' ? MSG_REVENUE_REQUIRED : MSG_REVENUE_FORMAT))
    }
    if(!NON_NEGATIVE_INTEGER.test(employeesInput.trim())) {
      cyberErrors.push(buildCyberError('EC337', employeesInput.trim() === '' ? MSG_EMPLOYEES_REQUIRED : MSG_EMPLOYEES_FORMAT))
    }
    if(cyberWebsiteError !== null) {
      cyberErrors.push(buildCyberError('EC338', cyberWebsiteErrorMessage))
    }
    return cyberErrors
  }

  const handleScrollOnMissingData = () => {
    const incorrectSections = validatePage()
    if(incorrectSections.length > 0) {
      document.getElementById(incorrectSections[0])?.scrollIntoView({ behavior: 'smooth' })
      setShowErrors(true)
    }

    // Surface every applicable cyber-quote error in the top-right validation panel. Because
    // triggerToast also populates the errors context, StandardInput renders the same messages
    // inline, keeping the panel and inline text in sync.
    const cyberErrors = buildCyberErrors()
    if(cyberErrors.length > 0) {
      triggerToast(cyberErrors)
    } else {
      clearToast()
    }
  }

  const handleUploadFile = async (files: File[]) => {
    let updatedRenewalData = {...renewalData}

    if(files.length > 0) {
      const uploadedFiles = await uploadFiles(files)
    
      setQuestions(prev => ({
        ...prev,
        hasGrievanceComplainFile: [...(prev?.hasGrievanceComplainFile ?? []), ...(uploadedFiles ?? [])]
      }))

      updatedRenewalData = {
        ...updatedRenewalData,
        renewalSection: {
          ...updatedRenewalData?.renewalSection,
          hasGrievanceComplainFile: [...(updatedRenewalData?.renewalSection?.hasGrievanceComplainFile ?? []), ...(uploadedFiles ?? [])]
        }
      }
    } else {
      setQuestions(prev => ({
        ...prev,
        hasGrievanceComplainFile: undefined
      }))

      updatedRenewalData = {
        ...updatedRenewalData,
        renewalSection: {
          ...updatedRenewalData?.renewalSection,
          hasGrievanceComplainFile: undefined
        }
      }
    }

    await saveRenewalData(updatedRenewalData)
  }

  const goBackRoute = () => {
    if(hasICOCAttorneys(updatedRenewalData())) {
      return '/customer/renew/attorneys/ic-oc'
    }

    if(allActiveAttorneys(updatedRenewalData()).length === 1) {
      return '/customer/renew/attorneys/sole-practitioner'
    }
    return '/customer/renew/attorneys/verify'
  }
  
  const goNextRoute = () => {
    if(isClaimsTriggered(updatedRenewalData()) === true) {
      return '/customer/renew/questions/claims'
    }
    
    return "/customer/renew/quote/select"
  }

  const revenueFormatError = questions?.wantsStandaloneCyberQuote === true && revenueInput.trim() !== '' && !POSITIVE_INTEGER.test(revenueInput.trim())
  const revenueRequiredError = questions?.wantsStandaloneCyberQuote === true && showErrors && revenueInput.trim() === ''
  const employeesFormatError = questions?.wantsStandaloneCyberQuote === true && employeesInput.trim() !== '' && !NON_NEGATIVE_INTEGER.test(employeesInput.trim())
  const employeesRequiredError = questions?.wantsStandaloneCyberQuote === true && showErrors && employeesInput.trim() === ''
  // "Required" waits for the first Save & Continue, exactly like the two fields above it — tabbing
  // through an empty field shouldn't shout. "Format" waits for blur instead of firing per keystroke:
  // unlike the digit rules, every correct URL passes through w / ww / www / www. / www.f on its way to
  // being valid, so live validation would flag the user for typing correctly. Both clear the instant
  // the value becomes good, because cyberWebsiteError is derived.
  const websiteRequiredError = cyberWebsiteError === 'required' && showErrors
  const websiteFormatError = (cyberWebsiteError === 'format' || cyberWebsiteError === 'length') && (websiteBlurred || showErrors)

  return (
    <ApplicationBodyContainer>
      <ApplicationBodyContent width="75%">
        <DefaultTitle>Please answer these questions about {firmData?.name} and its attorneys regarding the current year.</DefaultTitle>
        <DefaultSubtitle>Has anything changed since your last application?</DefaultSubtitle>
        <FormContainer maxWidth='760px'>
          <FormItemContainer maxWidth='100%'>
            <StepBullet id="step1" isComplete={validationPerStep.step1}>1</StepBullet>
            <OptionText
              title={
                <>
                  <strong>Since your last application, has any firm member acted as “Of Counsel” or Independent Contract Attorney to another law firm?</strong>
                  <br />
                  <small style={{ color: '#65666A', fontSize: '14px' }}>Please include members who are listed as such on your letterhead and/or website. <Link>Learn more</Link></small>
                </>
              }
              textHint='Please provide the name(s) of the attorney who is acting as such; the name and address of the other firm(s); their average hours per week in that capacity; and whether the other firm(s) are insured.'
              showOptions={true}
              text={questions?.hasICOCAttorneysDescription}
              setText={text => setQuestions(prev => ({ ...prev, hasICOCAttorneysDescription: text ?? undefined }))}
              selectedOption={questions?.hasICOCAttorneys}
              setSelectedOption={option => setQuestions(prev => ({ ...prev, hasICOCAttorneys: option }))}
              isIncorrect={showErrors && !validationPerStep.step1}
              errorCodes={stepOneErrors}
            />
          </FormItemContainer>
          <FormItemContainer maxWidth='100%'>
            <StepBullet id="step7" isComplete={validationPerStep.step7}>2</StepBullet>
            <OptionButtonsContainer labelSize='big'>
              <span>Since your last application, has the Firm or any Firm member filed any lawsuits against clients for fees?</span>
              <div>
                <OptionButton
                  checked={questions?.hasSueForFeesPolicy === true}
                  onChange={() => setQuestions({ ...questions, hasSueForFeesPolicy: true })}
                  isIncorrect={showErrors && !validationPerStep.step7 && (questions?.hasSueForFeesPolicy === undefined || questions?.hasSueForFeesPolicy === null)}
                  errorCode='EC77'
                >
                  Yes
                </OptionButton>
                <OptionButton
                  checked={questions?.hasSueForFeesPolicy === false}
                  onChange={() => setQuestions({
                    ...questions,
                    hasSueForFeesPolicy: false,
                    hasSueForFeesPolicyDescription: undefined
                  })}
                  isIncorrect={showErrors && !validationPerStep.step7 && (questions?.hasSueForFeesPolicy === undefined || questions?.hasSueForFeesPolicy === null)}
                  errorCode='EC77'
                >
                  No
                </OptionButton>
              </div>
              <ValidationErrorMessage errorCode='EC77' />
              {!validationPerStep.step7 && showErrors && (questions?.hasSueForFeesPolicy === undefined || questions?.hasSueForFeesPolicy === null) ? (
                  <FormInputErrorMessage>
                    This is a required field
                  </FormInputErrorMessage>
                ) : null}
              {(questions?.hasSueForFeesPolicy === true) ? (
                <ContainerWithLeftBar maxWidth='440px'>
                  <LeftBar />
                  <div className='content'>
                    <StandardInput
                      label="How many times?"
                      type="number"
                      value={questions?.hasSueForFeesPolicyDescription ?? ''}
                      onChange={({ target: { value } }) => setQuestions(prev => ({ ...prev, hasSueForFeesPolicyDescription: (isNaN(Number(value)) || !value) ? undefined : Number(value) }))}
                      width='25%'
                      isIncorrect={!validationPerStep.step7 && showErrors}
                      errorMessage={!validationPerStep.step7 && showErrors ? 'This is a required field' : ''}
                      errorCode='EC78'
                    />
                  </div>
                </ContainerWithLeftBar>
              ) : null}
            </OptionButtonsContainer>
          </FormItemContainer>
          <FormItemContainer maxWidth='100%'>
            <StepBullet id="step8" isComplete={validationPerStep.step8}>3</StepBullet>
            <OptionButtonsContainer labelSize='big'>
              <span style={{marginBottom: '4px'}}>Since your last application, have any of the firm’s lawyers completed a TLIE sponsored CLE course?</span>
              <small style={{ color: '#65666A', fontSize: '14px', marginBottom: '16px' }}>
                See available courses at: <Link href={tlieSources.preventionUrl}  rel="noreferrer noopener" target="_blank">{tlieSources.preventionUrl}</Link>.
                If yes, you may qualify for a discount. (Does not apply to New Attorney, Part-Time and Referral Service Only policies.)
              </small>
              <div>
                <OptionButton
                  checked={questions?.hasTlieSponsoredCle === true}
                  onChange={() => setQuestions({ ...questions, hasTlieSponsoredCle: true })}
                  isIncorrect={showErrors && !validationPerStep.step8 && (questions?.hasTlieSponsoredCle === undefined || questions?.hasTlieSponsoredCle === null)}
                  errorCode='EC79'
                >
                  Yes
                </OptionButton>
                <OptionButton
                  checked={questions?.hasTlieSponsoredCle === false}
                  onChange={() => setQuestions({ ...questions, hasTlieSponsoredCle: false })}
                  isIncorrect={showErrors && !validationPerStep.step8 && (questions?.hasTlieSponsoredCle === undefined || questions?.hasTlieSponsoredCle === null)}
                  errorCode='EC79'
                >
                  No
                </OptionButton>
              </div>
              <ValidationErrorMessage errorCode='EC79' />
              {!validationPerStep.step8 && showErrors && (questions?.hasTlieSponsoredCle === undefined || questions?.hasTlieSponsoredCle === null) ? (
                <FormInputErrorMessage>
                  This is a required field
                </FormInputErrorMessage>
              ) : null}
            </OptionButtonsContainer>
          </FormItemContainer>
          <FormItemContainer maxWidth='100%'>
            <StepBullet id="step9" isComplete={validationPerStep.step9}>4</StepBullet>
            <OptionButtonsContainer labelSize='big'>
              <span>Has the firm or any member been the subject of a legal malpractice claim in the past 10 years?</span>
              <div>
                <OptionButton
                  checked={questions?.hasMalPracticeClaims === true}
                  onChange={() => setQuestions({ ...questions, hasMalPracticeClaims: true })}
                  isIncorrect={showErrors && !validationPerStep.step9 && (questions?.hasMalPracticeClaims === undefined || questions?.hasMalPracticeClaims === null)}
                  errorCode='EC80'
                >
                  Yes
                </OptionButton>
                <OptionButton
                  checked={questions?.hasMalPracticeClaims === false}
                  onChange={() => setQuestions({
                    ...questions,
                    hasMalPracticeClaims: false,
                    hasMalPracticeClaimsReported: undefined,
                  })}
                  isIncorrect={showErrors && !validationPerStep.step9 && (questions?.hasMalPracticeClaims === undefined || questions?.hasMalPracticeClaims === null)}
                  errorCode='EC80'
                >
                  No
                </OptionButton>
              </div>
              <ValidationErrorMessage errorCode='EC80' />
              {!validationPerStep.step9 && showErrors && (questions?.hasMalPracticeClaims === undefined || questions?.hasMalPracticeClaims === null) ? (
                <FormInputErrorMessage>
                  This is a required field
                </FormInputErrorMessage>
              ) : null}
              {questions?.hasMalPracticeClaims === true ? (
                <ContainerWithLeftBar maxWidth='560px'>
                  <LeftBar/>
                  <div className='content'>
                    <span style={{margin: 0}}>Has the matter been previously reported to TLIE?</span>
                    <div>
                      <OptionButton
                        checked={questions?.hasMalPracticeClaimsReported === true}
                        onChange={() => setQuestions({ ...questions, hasMalPracticeClaimsReported: true })}
                        isIncorrect={showErrors && !validationPerStep.step9 && (questions?.hasMalPracticeBasisReported === undefined || questions?.hasMalPracticeBasisReported === null)}
                        errorCode='EC81'
                      >
                        Yes
                      </OptionButton>
                      <OptionButton
                        checked={questions?.hasMalPracticeClaimsReported === false}
                        onChange={() => setQuestions({ ...questions, hasMalPracticeClaimsReported: false })}
                        isIncorrect={showErrors && !validationPerStep.step9 && (questions?.hasMalPracticeBasisReported === undefined || questions?.hasMalPracticeBasisReported === null)}
                        errorCode='EC81'
                      >
                        No
                      </OptionButton>
                    </div>
                    <ValidationErrorMessage errorCode='EC81' />
                    {!validationPerStep.step9 && showErrors && (questions?.hasMalPracticeClaimsReported === undefined || questions?.hasMalPracticeClaimsReported === null) ? (
                      <FormInputErrorMessage>
                        This is a required field
                      </FormInputErrorMessage>
                    ) : null}
                    {questions?.hasMalPracticeClaimsReported === false ? (
                      <FlexDiv maxWidth='400px'>
                        <ReminderCard
                          icon='bulb'
                          title="Additional Information Required"
                        >
                          You will need to fill out additional Claim Supplement information on the next page regarding this answer.
                        </ReminderCard>
                      </FlexDiv>
                    ) : null}
                  </div>
                </ContainerWithLeftBar>
              ) : null}
            </OptionButtonsContainer>
          </FormItemContainer>
          <FormItemContainer maxWidth='100%'>
            <StepBullet id="step10" isComplete={validationPerStep.step10}>5</StepBullet>
            <OptionButtonsContainer labelSize='big'>
              <span>Does any firm member or employee (including non-attorneys) of the firm know, have reason to know, or have any basis to believe that a circumstance, act, error or omission might reasonably be expected to be the basis for a professional liability claim against the firm or any attorney?</span>
              <div>
                <OptionButton
                  checked={questions?.hasMalPracticeBasis === true}
                  onChange={() => setQuestions({ ...questions, hasMalPracticeBasis: true })}
                  isIncorrect={showErrors && !validationPerStep.step10 && (questions?.hasMalPracticeBasis === undefined || questions?.hasMalPracticeBasis === null)}
                  errorCode='EC82'
                >
                  Yes
                </OptionButton>
                <OptionButton
                  checked={questions?.hasMalPracticeBasis === false}
                  onChange={() => setQuestions({
                    ...questions,
                    hasMalPracticeBasis: false,
                    hasMalPracticeBasisReported: undefined
                  })}
                  isIncorrect={showErrors && !validationPerStep.step10 && (questions?.hasMalPracticeBasis === undefined || questions?.hasMalPracticeBasis === null)}
                  errorCode='EC82'
                >
                  No
                </OptionButton>
              </div>
              <ValidationErrorMessage errorCode='EC82' />
              {!validationPerStep.step10 && showErrors && (questions?.hasMalPracticeBasis === undefined || questions?.hasMalPracticeBasis === null) ? (
                <FormInputErrorMessage>
                  This is a required field
                </FormInputErrorMessage>
              ) : null}
              {questions?.hasMalPracticeBasis === true ? (
                <ContainerWithLeftBar maxWidth='560px'>
                  <LeftBar/>
                  <div className='content'>
                    <span style={{margin: 0}}>Has the matter been previously reported to TLIE?</span>
                    <div>
                      <OptionButton
                        checked={questions?.hasMalPracticeBasisReported === true}
                        onChange={() => setQuestions({ ...questions, hasMalPracticeBasisReported: true })}
                        isIncorrect={showErrors && !validationPerStep.step10 && (questions?.hasMalPracticeBasisReported === undefined || questions?.hasMalPracticeBasisReported === null)}
                        errorCode='EC83'
                      >
                        Yes
                      </OptionButton>
                      <OptionButton
                        checked={questions?.hasMalPracticeBasisReported === false}
                        onChange={() => setQuestions({ ...questions, hasMalPracticeBasisReported: false })}
                        isIncorrect={showErrors && !validationPerStep.step10 && (questions?.hasMalPracticeBasisReported === undefined || questions?.hasMalPracticeBasisReported === null)}
                        errorCode='EC83'
                      >
                        No
                      </OptionButton>
                    </div>
                    <ValidationErrorMessage errorCode='EC83' />
                    {!validationPerStep.step10 && showErrors && (questions?.hasMalPracticeBasisReported === undefined || questions?.hasMalPracticeBasisReported === null) ? (
                      <FormInputErrorMessage>
                        This is a required field
                      </FormInputErrorMessage>
                    ) : null}
                    {questions?.hasMalPracticeBasisReported === false ? (
                      <FlexDiv maxWidth='400px'>
                        <ReminderCard
                          icon='bulb'
                          title="Additional Information Required"
                        >
                          You will need to fill out additional Claim Supplement information on the next page regarding this answer.
                        </ReminderCard>
                      </FlexDiv>
                    ) : null}
                  </div>
                </ContainerWithLeftBar>
              ) : null}
            </OptionButtonsContainer>
          </FormItemContainer>
          <FormItemContainer maxWidth='100%'>
            <StepBullet id="step11" isComplete={validationPerStep.step11}>6</StepBullet>
            <OptionButtonsContainer labelSize='big'>
            <span>Has the Firm or any firm member, including attorneys who have joined the Firm, ever been the subject of a grievance complaint in the past five years?</span>
              <div>
                <OptionButton
                  checked={questions?.hasGrievanceComplain === true}
                  onChange={() => setQuestions({ ...questions, hasGrievanceComplain: true })}
                  isIncorrect={showErrors && !validationPerStep.step11 && (questions?.hasGrievanceComplain === undefined || questions?.hasGrievanceComplain === null)}
                  errorCode='EC84'
                >
                  Yes
                </OptionButton>
                <OptionButton
                  checked={questions?.hasGrievanceComplain === false}
                  onChange={() => setQuestions({
                    ...questions,
                    hasGrievanceComplain: false,
                    hasGrievanceComplainReported: undefined,
                    hasGrievanceComplainFile: undefined
                  })}
                  isIncorrect={showErrors && !validationPerStep.step11 && (questions?.hasGrievanceComplain === undefined || questions?.hasGrievanceComplain === null)}
                  errorCode='EC84'
                >
                  No
                </OptionButton>
              </div>
              <ValidationErrorMessage errorCode='EC84' />
              {!validationPerStep.step11 && showErrors && (questions?.hasGrievanceComplain === undefined || questions?.hasGrievanceComplain === null) ? (
                <FormInputErrorMessage>
                  This is a required field
                </FormInputErrorMessage>
              ) : null}
              {questions?.hasGrievanceComplain === true ? (
                <ContainerWithLeftBar maxWidth='560px'>
                  <LeftBar/>
                  <div className='content'>
                    <span style={{margin: 0}}>Has the matter been previously reported to TLIE?</span>
                    <div>
                      <OptionButton
                        checked={questions?.hasGrievanceComplainReported === true}
                        onChange={() => setQuestions({ ...questions, hasGrievanceComplainReported: true })}
                        isIncorrect={showErrors && !validationPerStep.step11 && (questions?.hasGrievanceComplainReported === undefined || questions?.hasGrievanceComplainReported === null)}
                        errorCode='EC85'
                      >
                        Yes
                      </OptionButton>
                      <OptionButton
                        checked={questions?.hasGrievanceComplainReported === false}
                        onChange={() => setQuestions({ ...questions, hasGrievanceComplainReported: false })}
                        isIncorrect={showErrors && !validationPerStep.step11 && (questions?.hasGrievanceComplainReported === undefined || questions?.hasGrievanceComplainReported === null)}
                        errorCode='EC85'
                      >
                        No
                      </OptionButton>
                    </div>
                    <ValidationErrorMessage errorCode='EC85' />
                    {!validationPerStep.step11 && showErrors && (questions?.hasGrievanceComplainReported === undefined || questions?.hasGrievanceComplainReported === null) ? (
                      <FormInputErrorMessage>
                        This is a required field
                      </FormInputErrorMessage>
                    ) : null}
                    {questions?.hasGrievanceComplainReported === false ? (
                      <FilePicker
                        title={"Attach a copy of grievance, description of circumstances, current status or updates since last application. " + {FILE_SIZE_LABEL}}
                        onFilesSelect={handleUploadFile}
                        selectedFiles={questions?.hasGrievanceComplainFile ? questions?.hasGrievanceComplainFile : []}
                        isIncorrect={showErrors && !validationPerStep.step11}
                        errorMessage={showErrors && !validationPerStep.step11 ? 'This is a required field' : ''}
                      />
                    ) : null}
                  </div>
                </ContainerWithLeftBar>
              ) : null}
            </OptionButtonsContainer>
          </FormItemContainer>
          <FormItemContainer maxWidth='100%'>
            <StepBullet id="step12" isComplete={validationPerStep.step12}>7</StepBullet>
            <OptionText
              title="Has any firm member ever voluntarily accepted discipline or been refused admission to the bar, reprimanded (privately or publicly), suspended from the practice of law, disbarred, received a probationary license or otherwise disciplined by any disciplinary or licensing authority?"
              textHint='Please provide full details'
              showOptions={true}
              text={questions?.hasVoluntarilyAcceptedDisciplineDescription}
              setText={text => setQuestions(prev => ({ ...prev, hasVoluntarilyAcceptedDisciplineDescription: text ?? undefined }))}
              selectedOption={questions?.hasVoluntarilyAcceptedDiscipline}
              setSelectedOption={option => setQuestions(prev => ({ ...prev, hasVoluntarilyAcceptedDiscipline: option }))}
              isIncorrect={showErrors && !validationPerStep.step12}
              errorCodes={stepTwelveErrors}
            />
          </FormItemContainer>
          <FormItemContainer maxWidth='100%'>
            <StepBullet id="step13" isComplete={validationPerStep.step13}>8</StepBullet>
            <OptionButtonsContainer labelSize='big'>
              <span>Would you like a separate quote for standalone cyber coverage from one of our partners? This is optional and separate from your Lawyers' Professional Liability quote.</span>
              <div>
                <OptionButton
                  checked={questions?.wantsStandaloneCyberQuote === true}
                  onChange={() => setQuestions(prev => ({ ...prev, wantsStandaloneCyberQuote: true }))}
                  isIncorrect={showErrors && !validationPerStep.step13 && (questions?.wantsStandaloneCyberQuote === undefined || questions?.wantsStandaloneCyberQuote === null)}
                  errorCode='EC335'
                >
                  Yes
                </OptionButton>
                <OptionButton
                  checked={questions?.wantsStandaloneCyberQuote === false}
                  onChange={() => {
                    clearToast()
                    setRevenueInput('')
                    setEmployeesInput('')
                    setWebsiteInput('')
                    setWebsiteBlurred(false)
                    setQuestions(prev => ({
                      ...prev,
                      wantsStandaloneCyberQuote: false,
                      cyberEstimatedAnnualRevenue: undefined,
                      cyberTotalEmployees: undefined,
                      cyberFirmWebsiteUrl: undefined
                    }))
                  }}
                  isIncorrect={showErrors && !validationPerStep.step13 && (questions?.wantsStandaloneCyberQuote === undefined || questions?.wantsStandaloneCyberQuote === null)}
                  errorCode='EC335'
                >
                  No
                </OptionButton>
              </div>
              <ValidationErrorMessage errorCode='EC335' />
              {!validationPerStep.step13 && showErrors && (questions?.wantsStandaloneCyberQuote === undefined || questions?.wantsStandaloneCyberQuote === null) ? (
                <FormInputErrorMessage>
                  This is a required field
                </FormInputErrorMessage>
              ) : null}
              {(questions?.wantsStandaloneCyberQuote === true) ? (
                <ContainerWithLeftBar maxWidth='490px'>
                  <LeftBar />
                  <div className='content'>
                    <StandardInput
                      label={<>What is your firm's estimated annual revenue?<br />You may round to the nearest ten thousand dollars.</>}
                      leftSymbol="$"
                      type="text"
                      inputMode="numeric"
                      value={revenueInput}
                      maxLength={REVENUE_MAX_DIGITS}
                      onChange={({ target: { value } }) => {
                        if(errors.length > 0) clearToast()
                        setRevenueInput(value)
                        setQuestions(prev => ({ ...prev, cyberEstimatedAnnualRevenue: POSITIVE_INTEGER.test(value.trim()) ? Number(value.trim()) : undefined }))
                      }}
                      isIncorrect={revenueFormatError || revenueRequiredError}
                      errorMessage={revenueFormatError
                        ? MSG_REVENUE_FORMAT
                        : (revenueRequiredError ? MSG_REVENUE_REQUIRED : '')}
                      errorCode='EC336'
                    />
                    <StandardInput
                      label="What is the total number of employees (including non-attorneys)?"
                      type="text"
                      inputMode="numeric"
                      value={employeesInput}
                      maxLength={EMPLOYEES_MAX_DIGITS}
                      onChange={({ target: { value } }) => {
                        if(errors.length > 0) clearToast()
                        setEmployeesInput(value)
                        setQuestions(prev => ({ ...prev, cyberTotalEmployees: NON_NEGATIVE_INTEGER.test(value.trim()) ? Number(value.trim()) : undefined }))
                      }}
                      isIncorrect={employeesFormatError || employeesRequiredError}
                      errorMessage={employeesFormatError
                        ? MSG_EMPLOYEES_FORMAT
                        : (employeesRequiredError ? MSG_EMPLOYEES_REQUIRED : '')}
                      errorCode='EC337'
                    />
                    <StandardInput
                      label={<>What is your firm's website?<br />Please start with www. — for example, www.yourfirm.com.</>}
                      value={websiteInput}
                      onChange={({ target: { value } }) => {
                        if(errors.length > 0) clearToast()
                        setWebsiteInput(value)
                      }}
                      // Rewrite the visible text to the stored form once the user is done with the
                      // field and still looking at it — never mid-keystroke, which would eat the
                      // characters of "https://" as they are typed.
                      handleBlur={() => {
                        setWebsiteBlurred(true)
                        setWebsiteInput(current => normalizeCyberWebsite(current))
                      }}
                      isIncorrect={websiteFormatError || websiteRequiredError}
                      errorMessage={(websiteFormatError || websiteRequiredError) ? cyberWebsiteErrorMessage : ''}
                      errorCode='EC338'
                    />
                  </div>
                </ContainerWithLeftBar>
              ) : null}
            </OptionButtonsContainer>
          </FormItemContainer>
        </FormContainer>
      </ApplicationBodyContent>
      <PrebuiltRenewalFooter
        goBackRoute={goBackRoute()}
        isFormValid={Object.keys(validationPerStep).every(step => validationPerStep[step] === true)}
        nextRoute={goNextRoute()}
        updatedApplication={updatedRenewalData()}
        beforeSaveContinueClick={handleScrollOnMissingData}
      />
    </ApplicationBodyContainer>
  )
}

export default Questions